/**
 * useTemplateEditor Hook
 *
 * Manages the state and operations for editing a single template.
 * Works with the V2 denormalized template system where sections
 * have inline content (no module resolution needed).
 */

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useEntitlementStatus } from '@features/entitlements';
import { useEventTracking } from '@services/statsig';
import { decodeHtmlEntities } from '@utils/htmlEntities';
import { useSnackbar } from 'notistack';

import {
  useForkTemplateMutation,
  useUpdateSectionMutation,
  useUpdateTemplateMutation,
} from '../store/api';
import type { ProviderPreviousNoteScope, SectionWithMeta, TemplateWithMeta } from '../types';

interface UseTemplateEditorProps {
  /** Template to edit */
  template: TemplateWithMeta | null;
  /** Callback when template is saved */
  onSave?: () => void;
  /** Callback when a copy is created (transparent copy-on-write) */
  onTemplateForked?: (newTemplateId: string) => void;
}

interface UseTemplateEditorReturn {
  /** Template name (editable) */
  templateName: string;
  /** Template description */
  templateDescription: string | null;
  /** Template sections with metadata */
  sections: SectionWithMeta[];
  /** Which earlier note may be carried forward */
  previousNoteScope: ProviderPreviousNoteScope;
  /** Whether template has unsaved changes */
  hasChanges: boolean;
  /** Whether title is being edited */
  isTitleEditing: boolean;
  /** Whether save is in progress */
  isSaving: boolean;
  /** Last save timestamp */
  lastSaved: Date | null;
  /** Whether user can edit this template */
  canEdit: boolean;
  /** Whether user can copy this template */
  canFork: boolean;
  /** Start editing title */
  startEditingTitle: () => void;
  /** Update title */
  updateTitle: (title: string) => void;
  /** Finish editing title (saves automatically) */
  finishEditingTitle: () => Promise<void>;
  /** Update description */
  updateDescription: (description: string) => void;
  /** Update earlier-note behavior */
  updatePreviousNoteScope: (value: ProviderPreviousNoteScope) => void;
  /** Add section at index */
  addSection: (section: Omit<SectionWithMeta, 'showPreview'>, index?: number) => void;
  /** Remove section */
  removeSection: (sectionId: string) => void;
  /** Reorder sections */
  reorderSections: (startIndex: number, endIndex: number) => void;
  /** Update section content */
  updateSectionContent: (sectionId: string, content: string) => Promise<boolean>;
  /** Update section title */
  updateSectionTitle: (sectionId: string, title: string) => void;
  /** Save all changes */
  save: () => Promise<boolean>;
  /** Reset to original state */
  reset: () => void;
}

interface TemplateSaveSnapshot {
  name: string;
  description: string | null;
  sections: SectionWithMeta[];
  previousNoteScope: ProviderPreviousNoteScope;
}

interface TemplateWriteRequest {
  perform: (targetTemplateId: string) => Promise<unknown>;
  onSuccess?: () => void;
  onError?: (error: unknown) => void;
  shouldRun?: () => boolean;
}

interface ForkInFlight {
  generation: number;
  sourceTemplateId: string;
  promise: Promise<string | null>;
}

export const useTemplateEditor = ({
  template,
  onSave,
  onTemplateForked,
}: UseTemplateEditorProps): UseTemplateEditorReturn => {
  const { template: templateMetrics } = useEventTracking();
  const { enqueueSnackbar } = useSnackbar();

  // Mutations
  const [updateTemplateMutation, { isLoading: isUpdating }] = useUpdateTemplateMutation();
  const [updateSectionMutation, { isLoading: isUpdatingSection }] = useUpdateSectionMutation();
  const [forkMutation, { isLoading: isForking }] = useForkTemplateMutation();

  // State
  const [templateName, setTemplateName] = useState('');
  const [templateDescription, setTemplateDescription] = useState<string | null>(null);
  const [sections, setSections] = useState<SectionWithMeta[]>([]);
  const [previousNoteScope, setPreviousNoteScope] = useState<ProviderPreviousNoteScope>('any_note');
  const [isTitleEditing, setIsTitleEditing] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);

  // Track original state for change detection
  const originalStateRef = useRef<{
    name: string;
    description: string | null;
    sections: SectionWithMeta[];
    previousNoteScope: ProviderPreviousNoteScope;
  } | null>(null);
  const hasTrackedStart = useRef(false);

  // Pending autosave flag
  const pendingAutosaveRef = useRef(false);

  // Track if we've already forked during this session
  const forkedTemplateIdRef = useRef<string | null>(null);
  const forkedSectionIdMapRef = useRef<Map<string, string>>(new Map());
  const pendingSectionContentRef = useRef<Map<string, string>>(new Map());
  const forkInFlightRef = useRef<ForkInFlight | null>(null);

  // Template-scoped lifecycle and write ordering.
  const activeTemplateIdRef = useRef<string | null>(template?.template.id ?? null);
  const templateGenerationRef = useRef(0);
  const writeQueueRef = useRef<Promise<void>>(Promise.resolve());
  const latestAutosaveVersionRef = useRef(0);

  const activeTemplateId = template?.template.id ?? null;
  useLayoutEffect(() => {
    if (activeTemplateIdRef.current === activeTemplateId) return;

    activeTemplateIdRef.current = activeTemplateId;
    templateGenerationRef.current += 1;
    forkedTemplateIdRef.current = null;
    forkedSectionIdMapRef.current = new Map();
    pendingSectionContentRef.current = new Map();
    forkInFlightRef.current = null;
    pendingAutosaveRef.current = false;
    latestAutosaveVersionRef.current += 1;
    writeQueueRef.current = Promise.resolve();
  }, [activeTemplateId]);

  // Get entitlement status for template creation
  const { isBlocked: isTemplateBlocked } = useEntitlementStatus('template_creation');

  // Permissions from template
  const canEdit = template?.permissions.can_edit ?? false;
  // Override can_fork when the authoritative entitlement is depleted.
  const canFork = (template?.permissions.can_fork ?? false) && !isTemplateBlocked;

  // Transform template sections to SectionWithMeta
  const transformSections = useCallback(
    (templateSections: TemplateWithMeta['revision']['sections']): SectionWithMeta[] => {
      if (!templateSections) return [];
      return templateSections.map((section, index) => ({
        id: section.id,
        // Decode any HTML-encoded characters (e.g. "&amp;" -> "&") so titles
        // like "Assessment & Plan" render literally. See GDN-2914.
        title: decodeHtmlEntities(section.title),
        order: section.order ?? index,
        content: section.content,
        legacyModuleId: section.legacy_module_id,
        exampleOutput: section.example_output ?? null,
        showPreview: false,
      }));
    },
    [],
  );

  // Transparent copy-on-write: check if we need to create a copy before editing
  const needsCopyOnWrite = !canEdit && canFork;

  /**
   * Helper to ensure the user has their own editable copy of the template.
   * If they are editing a read-only template that allows copying, this will
   * trigger the copy (fork) operation.
   */
  const ensureCopyIfNeeded = useCallback(async (): Promise<string | null> => {
    // Already editable or no template selected
    if (!needsCopyOnWrite || !template) {
      return template?.template.id || null;
    }

    const generation = templateGenerationRef.current;
    const sourceTemplateId = template.template.id;
    const isActive = () =>
      templateGenerationRef.current === generation &&
      activeTemplateIdRef.current === sourceTemplateId;

    // Already forked in this session
    if (forkedTemplateIdRef.current) {
      return forkedTemplateIdRef.current;
    }

    let inFlight = forkInFlightRef.current;
    if (
      !inFlight ||
      inFlight.generation !== generation ||
      inFlight.sourceTemplateId !== sourceTemplateId
    ) {
      const promise = (async () => {
        try {
          const result = await forkMutation({
            templateId: sourceTemplateId,
            forkNoteTemplateRequest: {},
          }).unwrap();
          if (!isActive()) return null;

          const newTemplateId = result.template.id;
          forkedTemplateIdRef.current = newTemplateId;

          // Update local state with the new template's data immediately
          // This ensures that subsequent section updates use the new IDs
          const transformedSections = transformSections(result.revision.sections);
          const sectionIdMap = new Map<string, string>();
          sections.forEach((section, index) => {
            const forkedSection = transformedSections[index];
            if (forkedSection) {
              sectionIdMap.set(section.id, forkedSection.id);
            }
          });
          forkedSectionIdMapRef.current = sectionIdMap;
          setSections(transformedSections);

          // Also update original state to match the new template
          if (originalStateRef.current) {
            originalStateRef.current = {
              name: decodeHtmlEntities(result.revision.title),
              description: result.revision.description ?? null,
              sections: transformedSections,
              previousNoteScope: result.previous_note_scope ?? 'any_note',
            };
          }

          templateMetrics.customizationStarted(newTemplateId, {
            source: 'public_copy',
            initial_sections_count: result.revision.sections?.length || 0,
          });

          enqueueSnackbar('Template copied to your templates', { variant: 'success' });
          onTemplateForked?.(newTemplateId);
          return newTemplateId;
        } catch (error) {
          if (!isActive()) return null;

          console.error('Failed to copy template:', error);
          enqueueSnackbar('Failed to copy template', { variant: 'error' });
          return null;
        }
      })();
      inFlight = { generation, sourceTemplateId, promise };
      forkInFlightRef.current = inFlight;
    }

    try {
      return await inFlight.promise;
    } finally {
      if (forkInFlightRef.current === inFlight) {
        forkInFlightRef.current = null;
      }
    }
  }, [
    needsCopyOnWrite,
    template,
    forkMutation,
    transformSections,
    sections,
    templateMetrics,
    enqueueSnackbar,
    onTemplateForked,
  ]);

  const enqueueTemplateWrite = useCallback(
    (request: TemplateWriteRequest): Promise<boolean> => {
      const generation = templateGenerationRef.current;
      const sourceTemplateId = activeTemplateIdRef.current;
      const isActive = () =>
        templateGenerationRef.current === generation &&
        activeTemplateIdRef.current === sourceTemplateId;

      const run = async (): Promise<boolean> => {
        if (!isActive() || request.shouldRun?.() === false) return false;

        const targetTemplateId = await ensureCopyIfNeeded();
        if (!targetTemplateId || !isActive() || request.shouldRun?.() === false) {
          return false;
        }

        try {
          await request.perform(targetTemplateId);
          if (!isActive()) return false;

          request.onSuccess?.();
          return true;
        } catch (error) {
          if (isActive()) {
            request.onError?.(error);
          }
          return false;
        }
      };

      const result = writeQueueRef.current.then(run, run);
      writeQueueRef.current = result.then(() => undefined);
      return result;
    },
    [ensureCopyIfNeeded],
  );

  // Calculate if there are unsaved changes
  const hasChanges = (() => {
    if (!originalStateRef.current) return false;

    const nameChanged = templateName !== originalStateRef.current.name;
    const descriptionChanged = templateDescription !== originalStateRef.current.description;
    const sectionsChanged =
      JSON.stringify(sections) !== JSON.stringify(originalStateRef.current.sections);
    const previousNoteScopeChanged =
      previousNoteScope !== originalStateRef.current.previousNoteScope;

    return nameChanged || descriptionChanged || sectionsChanged || previousNoteScopeChanged;
  })();

  // Initialize state from template
  useEffect(() => {
    if (template) {
      const decodedTemplateName = decodeHtmlEntities(template.revision.title);
      const transformedSections = transformSections(template.revision.sections);
      setTemplateName(decodedTemplateName);
      setTemplateDescription(template.revision.description ?? null);
      setSections(transformedSections);
      const nextPreviousNoteScope = template.previousNoteScope ?? 'any_note';
      setPreviousNoteScope(nextPreviousNoteScope);
      originalStateRef.current = {
        name: decodedTemplateName,
        description: template.revision.description ?? null,
        sections: transformedSections,
        previousNoteScope: nextPreviousNoteScope,
      };
      setLastSaved(null);
      hasTrackedStart.current = false;
    }
  }, [template, transformSections]);

  // Track customization start
  useEffect(() => {
    if (!hasTrackedStart.current && template && canEdit) {
      templateMetrics.customizationStarted(template.template.id, {
        source: 'existing',
        initial_sections_count: template.revision.sections?.length || 0,
      });
      hasTrackedStart.current = true;
    }
  }, [template, canEdit, templateMetrics]);

  // Start editing title
  const startEditingTitle = useCallback(() => {
    if (canEdit || canFork) {
      setIsTitleEditing(true);
    }
  }, [canEdit, canFork]);

  // Update title
  const updateTitle = useCallback((title: string) => {
    setTemplateName(title);
  }, []);

  // Finish editing title - auto-save if changed
  const finishEditingTitle = useCallback(async () => {
    setIsTitleEditing(false);

    if (!template || !(canEdit || canFork)) return;

    const trimmedName = templateName.trim();
    if (trimmedName !== originalStateRef.current?.name) {
      await enqueueTemplateWrite({
        perform: async targetTemplateId => {
          templateMetrics.sectionModified(targetTemplateId, {
            action: 'edit_title',
            section_type: 'template_title',
          });
          await updateTemplateMutation({
            templateId: targetTemplateId,
            updateNoteTemplateRequest: {
              title: trimmedName,
            },
          }).unwrap();
        },
        onSuccess: () => {
          if (originalStateRef.current) {
            originalStateRef.current.name = trimmedName;
          }
          setLastSaved(new Date());
          onSave?.();
        },
        onError: error => {
          console.error('Failed to save template title:', error);
          enqueueSnackbar('Failed to save template title', { variant: 'error' });
        },
      });
    }
  }, [
    template,
    canEdit,
    canFork,
    templateName,
    enqueueTemplateWrite,
    templateMetrics,
    updateTemplateMutation,
    onSave,
    enqueueSnackbar,
  ]);

  // Update description
  const updateDescription = useCallback((description: string) => {
    setTemplateDescription(description);
    pendingAutosaveRef.current = true;
  }, []);

  const updatePreviousNoteScope = useCallback(
    (nextValue: ProviderPreviousNoteScope) => {
      if (!(canEdit || canFork)) return;
      setPreviousNoteScope(nextValue);
      pendingAutosaveRef.current = true;
    },
    [canEdit, canFork],
  );

  // Add section
  const addSection = useCallback(
    (section: Omit<SectionWithMeta, 'showPreview'>, index?: number) => {
      if (!(canEdit || canFork)) return;

      const newSection: SectionWithMeta = {
        ...section,
        showPreview: false,
      };

      setSections(prev => {
        const updated = [...prev];
        if (index !== undefined && index !== null) {
          updated.splice(index, 0, newSection);
        } else {
          updated.push(newSection);
        }
        // Update order values
        return updated.map((s, i) => ({ ...s, order: i }));
      });

      if (template) {
        templateMetrics.addSection(forkedTemplateIdRef.current || template.template.id, {
          section_type: 'custom',
        });
      }

      pendingAutosaveRef.current = true;
    },
    [canEdit, canFork, template, templateMetrics],
  );

  // Remove section
  const removeSection = useCallback(
    (sectionId: string) => {
      if (!(canEdit || canFork)) return;

      setSections(prev => {
        const updated = prev.filter(s => s.id !== sectionId);
        // Update order values
        return updated.map((s, i) => ({ ...s, order: i }));
      });

      if (template) {
        templateMetrics.removeSection(forkedTemplateIdRef.current || template.template.id, {
          section_type: 'custom',
        });
      }

      pendingAutosaveRef.current = true;
    },
    [canEdit, canFork, template, templateMetrics],
  );

  // Reorder sections
  const reorderSections = useCallback(
    (startIndex: number, endIndex: number) => {
      if (!(canEdit || canFork) || startIndex === endIndex) return;

      setSections(prev => {
        const result = [...prev];
        const [removed] = result.splice(startIndex, 1);
        result.splice(endIndex, 0, removed);
        // Update order values
        return result.map((s, i) => ({ ...s, order: i }));
      });

      if (template) {
        templateMetrics.sectionModified(forkedTemplateIdRef.current || template.template.id, {
          action: 'reorder',
          section_position: endIndex,
        });
      }

      pendingAutosaveRef.current = true;
    },
    [canEdit, canFork, template, templateMetrics],
  );

  // Update section content (uses dedicated endpoint)
  const updateSectionContent = useCallback(
    async (sectionId: string, content: string): Promise<boolean> => {
      if (!template || !(canEdit || canFork)) return false;

      const previousContent = sections.find(section => section.id === sectionId)?.content;
      let targetSectionId = sectionId;
      pendingSectionContentRef.current.set(sectionId, content);

      setSections(previousSections =>
        previousSections.map(section =>
          section.id === sectionId ? { ...section, content } : section,
        ),
      );

      return enqueueTemplateWrite({
        perform: async targetTemplateId => {
          targetSectionId = forkedSectionIdMapRef.current.get(sectionId) ?? sectionId;
          await updateSectionMutation({
            templateId: targetTemplateId,
            sectionId: targetSectionId,
            updateSectionContentRequest: { content },
          }).unwrap();
        },
        onSuccess: () => {
          const isLatestContent = pendingSectionContentRef.current.get(sectionId) === content;
          if (isLatestContent) {
            pendingSectionContentRef.current.delete(sectionId);
            setSections(previousSections =>
              previousSections.map(section =>
                section.id === targetSectionId ? { ...section, content } : section,
              ),
            );
          }
          if (originalStateRef.current) {
            originalStateRef.current.sections = originalStateRef.current.sections.map(section =>
              section.id === targetSectionId ? { ...section, content } : section,
            );
          }
          setLastSaved(new Date());
        },
        onError: error => {
          const isLatestContent = pendingSectionContentRef.current.get(sectionId) === content;
          if (isLatestContent) {
            pendingSectionContentRef.current.delete(sectionId);
          }
          if (isLatestContent && previousContent !== undefined) {
            setSections(previousSections =>
              previousSections.map(section =>
                section.id === sectionId && section.content === content
                  ? { ...section, content: previousContent }
                  : section,
              ),
            );
          }
          console.error('Failed to update section content:', error);
          enqueueSnackbar('Failed to save section', { variant: 'error' });
        },
      });
    },
    [
      template,
      canEdit,
      canFork,
      enqueueTemplateWrite,
      sections,
      updateSectionMutation,
      enqueueSnackbar,
    ],
  );

  // Update section title (local only, saved with full save)
  const updateSectionTitle = useCallback(
    (sectionId: string, title: string) => {
      if (!(canEdit || canFork)) return;

      setSections(prev => prev.map(s => (s.id === sectionId ? { ...s, title } : s)));

      pendingAutosaveRef.current = true;
    },
    [canEdit, canFork],
  );

  // Save all changes
  const save = useCallback(async (): Promise<boolean> => {
    if (!template || !(canEdit || canFork)) return false;

    const snapshot: TemplateSaveSnapshot = {
      name: templateName,
      description: templateDescription,
      sections: sections.map(section => ({ ...section })),
      previousNoteScope,
    };
    let persistedSnapshot = snapshot;

    return enqueueTemplateWrite({
      perform: async targetTemplateId => {
        persistedSnapshot = {
          ...snapshot,
          sections: snapshot.sections.map(section => ({
            ...section,
            id: forkedSectionIdMapRef.current.get(section.id) ?? section.id,
          })),
        };
        await updateTemplateMutation({
          templateId: targetTemplateId,
          updateNoteTemplateRequest: {
            title: snapshot.name,
            description: snapshot.description,
            sections: persistedSnapshot.sections.map(section => ({
              id: section.id,
              title: section.title,
              order: section.order,
              content: section.content,
              example_output: section.exampleOutput,
            })),
            previous_note_scope: snapshot.previousNoteScope,
          },
        }).unwrap();
      },
      onSuccess: () => {
        setLastSaved(new Date());
        originalStateRef.current = persistedSnapshot;
        pendingAutosaveRef.current = false;
        enqueueSnackbar('Template saved successfully', { variant: 'success' });
        onSave?.();
      },
      onError: error => {
        console.error('Failed to save template:', error);
        enqueueSnackbar('Failed to save template', { variant: 'error' });
      },
    });
  }, [
    template,
    canEdit,
    canFork,
    enqueueTemplateWrite,
    templateName,
    templateDescription,
    sections,
    previousNoteScope,
    updateTemplateMutation,
    onSave,
    enqueueSnackbar,
  ]);

  const enqueueAutosave = useCallback(
    (snapshot: TemplateSaveSnapshot) => {
      const version = latestAutosaveVersionRef.current + 1;
      latestAutosaveVersionRef.current = version;
      let persistedSnapshot = snapshot;

      void enqueueTemplateWrite({
        shouldRun: () => version === latestAutosaveVersionRef.current,
        perform: async targetTemplateId => {
          persistedSnapshot = {
            ...snapshot,
            sections: snapshot.sections.map(section => ({
              ...section,
              id: forkedSectionIdMapRef.current.get(section.id) ?? section.id,
            })),
          };
          await updateTemplateMutation({
            templateId: targetTemplateId,
            updateNoteTemplateRequest: {
              title: snapshot.name,
              description: snapshot.description,
              sections: persistedSnapshot.sections.map(section => ({
                id: section.id,
                title: section.title,
                order: section.order,
                content: section.content,
                example_output: section.exampleOutput,
              })),
              previous_note_scope: snapshot.previousNoteScope,
            },
          }).unwrap();
        },
        onSuccess: () => {
          setLastSaved(new Date());
          originalStateRef.current = persistedSnapshot;
        },
        onError: error => {
          console.error('Autosave failed:', error);
        },
      });
    },
    [enqueueTemplateWrite, updateTemplateMutation],
  );

  // Autosave when pending changes exist
  useEffect(() => {
    if (!pendingAutosaveRef.current || !template || !(canEdit || canFork)) {
      return;
    }

    pendingAutosaveRef.current = false;
    enqueueAutosave({
      name: templateName,
      description: templateDescription,
      sections: sections.map(section => ({ ...section })),
      previousNoteScope,
    });
  }, [
    sections,
    template,
    canEdit,
    canFork,
    templateName,
    templateDescription,
    previousNoteScope,
    enqueueAutosave,
  ]);

  // Reset to original state
  const reset = useCallback(() => {
    if (originalStateRef.current) {
      setTemplateName(originalStateRef.current.name);
      setTemplateDescription(originalStateRef.current.description);
      setSections([...originalStateRef.current.sections]);
      setPreviousNoteScope(originalStateRef.current.previousNoteScope);
    }
  }, []);

  return {
    templateName,
    templateDescription,
    sections,
    previousNoteScope,
    hasChanges,
    isTitleEditing,
    isSaving: isUpdating || isUpdatingSection || isForking,
    lastSaved,
    canEdit,
    canFork,
    startEditingTitle,
    updateTitle,
    finishEditingTitle,
    updateDescription,
    updatePreviousNoteScope,
    addSection,
    removeSection,
    reorderSections,
    updateSectionContent,
    updateSectionTitle,
    save,
    reset,
  };
};
